import React from 'react';
import { OHeroVideoProps } from './OHeroVideo.types';
import * as Styled from './OHeroVideo.styles';
import { AVideo } from '../../atoms/AVideo/index';
import { AVideoType, AVideoAspectRatio } from '../../atoms/AVideo/AVideo.types';
import { AScrimGradient } from '../../atoms/AScrimGradient/index';
import { AScrimGradientDirection } from '../../atoms/AScrimGradient/AScrimGradient.types';
import { AHeaderTag } from '../../atoms/AHeader/AHeader.types';
import { MBrandElement, MBrandElementType } from '../../molecules/MBrandElement/index';
import { MTextBlockSimple, MTextBlockSimpleAlignment } from '../../molecules/MTextBlockSimple/index';
import { MButton, MButtonType, MButtonSize } from '../../molecules/MButton/index';
import { MButtonOverlay } from '../../molecules/MButtonOverlay/index';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OHeroVideo';
const TEST_ID = 'mi-web-ui-kit-OHeroVideo';

export const OHeroVideo: React.FC<OHeroVideoProps> = ({
  contentAlignment = 'Bottom',
  imageScale = 'Large',
  logo = true,
  secondaryButton = false,
  aVideo,
  aScrimGradient,
  mBrandElement,
  mTextBlockSimple,
  mButton,
  mButtonOverlay,
  className,
  ...rest
}) => {
  const getAlignmentClass = () => {
    switch (contentAlignment) {
      case 'Center':
        return 'center';
      case 'Left':
        return 'left';
      case 'Bottom':
      default:
        return 'bottom';
    }
  };

  const getImageScale = (): 'Small' | 'Large' => {
    return imageScale === 'Small' ? 'Small' : 'Large';
  };

  const getScrimDirection = () => {
    if (!aScrimGradient?.direction) {
      return contentAlignment === 'Bottom' ? AScrimGradientDirection.BottomToTop : AScrimGradientDirection.LeftToRight;
    }

    const directionMap: Record<string, AScrimGradientDirection> = {
      'Top to Bottom': AScrimGradientDirection.TopToBottom,
      'Bottom to Top': AScrimGradientDirection.BottomToTop,
      'Left to Right': AScrimGradientDirection.LeftToRight,
      'Right to Left': AScrimGradientDirection.RightToLeft,
    };

    return directionMap[aScrimGradient.direction] || AScrimGradientDirection.BottomToTop;
  };

  const getVideoAspectRatio = () => {
    if (!aVideo?.aspectRatio) return AVideoAspectRatio.Ratio16x9;

    const ratioMap: Record<string, AVideoAspectRatio> = {
      '↔︎ 1:1': AVideoAspectRatio.Ratio1x1,
      '↕︎ 2:3': AVideoAspectRatio.Ratio2x3,
      '↔︎ 5:2': AVideoAspectRatio.Ratio5x2,
      '↔︎ 16:9': AVideoAspectRatio.Ratio16x9,
      '↔︎ 3:2': AVideoAspectRatio.Ratio3x2,
    };

    return ratioMap[aVideo.aspectRatio] || AVideoAspectRatio.Ratio16x9;
  };

  const getTextAlignment = () => {
    if (mTextBlockSimple?.alignment === MTextBlockSimpleAlignment.Center) {
      return MTextBlockSimpleAlignment.Center;
    }
    return MTextBlockSimpleAlignment.Left;
  };

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={className}
      $contentAlignment={getAlignmentClass()}
      $imageScale={getImageScale()}
      {...rest}
    >
      <Styled.VideoContainer>
        {aVideo && aVideo.videoId && aVideo.videoAccountId && (
          <AVideo
            videoType={AVideoType.Brightcove}
            aspectRatio={getVideoAspectRatio()}
            videoId={aVideo.videoId}
            videoAccountId={aVideo.videoAccountId}
            playerId={aVideo.playerId}
            autoPlay={true}
            muted={true}
            loop={true}
            controls={false}
          />
        )}
        {aScrimGradient && (
          <Styled.ScrimContainer>
            <AScrimGradient direction={getScrimDirection()} />
          </Styled.ScrimContainer>
        )}
      </Styled.VideoContainer>

      <Styled.ContentContainer $contentAlignment={getAlignmentClass()}>
        <Styled.ContentInner $contentAlignment={getAlignmentClass()}>
          {logo && mBrandElement && (
            <Styled.BrandElementWrapper $contentAlignment={getAlignmentClass()}>
              <MBrandElement
                type={mBrandElement.type as MBrandElementType}
                aIcon={mBrandElement.aIcon}
                aImage={mBrandElement.aImage}
                mLogoContainer={mBrandElement.mLogoContainer}
              />
            </Styled.BrandElementWrapper>
          )}

          {mTextBlockSimple && (
            <Styled.TextBlockWrapper $contentAlignment={getAlignmentClass()}>
              <MTextBlockSimple
                alignment={getTextAlignment()}
                eyebrowText={mTextBlockSimple.aEyebrow?.eyebrowText}
                headerText={mTextBlockSimple.aHeader?.text}
                headerSize={mTextBlockSimple.aHeader?.size}
                headerTag={mTextBlockSimple.aHeader?.headerTag as AHeaderTag}
                bodyText={mTextBlockSimple.aBody?.bodyText}
              />
            </Styled.TextBlockWrapper>
          )}

          <Styled.ButtonGroup $contentAlignment={getAlignmentClass()}>
            {mButton && (
              <MButton
                buttonText={mButton.buttonText}
                buttonType={mButton.type as MButtonType}
                size={mButton.size as MButtonSize}
                ctaLink={mButton.ctaLink}
                openInANewTab={mButton.openInANewTab === 'true' || mButton.openInANewTab === true}
                aIcon={mButton.aIcon}
              />
            )}

            {secondaryButton && mButtonOverlay && (
              <Styled.OverlayButtonWrapper>
                <MButtonOverlay
                  text={mButtonOverlay.text}
                  aIcon={mButtonOverlay.aIcon}
                  imageOverlay={mButtonOverlay.imageOverlay === true}
                  iconOnly={mButtonOverlay.iconOnly === true}
                />
              </Styled.OverlayButtonWrapper>
            )}
          </Styled.ButtonGroup>
        </Styled.ContentInner>
      </Styled.ContentContainer>
    </Styled.Wrapper>
  );
};
